import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

function ChatTimerMaxPauseLimitConfig() {
    const BASE_URL = process.env.REACT_APP_BASEURL;
    const getAuthToken = () => localStorage.getItem("astroVani-admin-token");

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            chatTimerMaxPauseLimit: 0
        }
    });

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${BASE_URL}/admin/chatTimerMaxPauseLimit`, {
                    headers: {
                        'Authorization': getAuthToken(),
                    },
                });

                if (!response.ok) throw new Error("Failed to fetch chat timer max pause limit");
                const data = await response.json();

                // Assuming the structure is similar to other config endpoints, e.g., data.data.value
                const configData = data?.data?.value ?? data?.data;

                if (configData !== undefined && configData !== null) {
                    reset({
                        chatTimerMaxPauseLimit: configData
                    });
                }
            } catch (error) {
                console.error("Fetch error:", error);
                toast.error(error.message || "Failed to fetch details");
            }
        };

        fetchConfig();
    }, [BASE_URL, reset]);

    const onSubmit = async (data) => {
        try {
            const valStr = String(data.chatTimerMaxPauseLimit);
            if (valStr.includes('.') || !/^\d+$/.test(valStr)) {
                toast.error("Only integer values are allowed (no decimals).");
                return;
            }

            const payload = {
                key: "chatTimerMaxPauseLimit",
                value: parseInt(data.chatTimerMaxPauseLimit, 10),
                description: "Maximum pause time in seconds for a chat consultation when viewing a Pooja details page"
            };

            const response = await fetch(`${BASE_URL}/admin/chatTimerMaxPauseLimit`, {
                method: 'PATCH',
                headers: {
                    'Authorization': getAuthToken(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (!response.ok || (result && result.success === false) || (result && result.status === 'error')) {
                throw new Error(result.message || "Failed to update config");
            }

            toast.success(result.message || "Config updated successfully");
        } catch (error) {
            console.error("Update error:", error);
            toast.error(error.message || "Failed to update config");
        }
    };

    return (
        <div className="card">
            <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row g-3 align-items-center">
                        <div className="col-md-12">
                            <label className="form-label"> Chat Timer Max Pause Limit (Seconds)</label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    step="1"
                                    onKeyDown={(e) => {
                                        if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onPaste={(e) => {
                                        const pasteData = e.clipboardData.getData('text');
                                        if (pasteData.includes('.') || !/^\d+$/.test(pasteData.trim())) {
                                            e.preventDefault();
                                            toast.error("Only whole integer numbers are allowed");
                                        }
                                    }}
                                    className={`form-control ${errors.chatTimerMaxPauseLimit ? 'is-invalid' : ''}`}
                                    placeholder="Enter maximum pause time in seconds"
                                    {...register('chatTimerMaxPauseLimit', {
                                        required: "Pause time limit is required",
                                        min: { value: 0, message: "Minimum is 0" },
                                        validate: (val) => {
                                            if (val === '' || val === null || val === undefined) return true;
                                            if (String(val).includes('.') || !/^\d+$/.test(String(val))) {
                                                return "Only integer values are allowed (no decimals)";
                                            }
                                            return true;
                                        }
                                    })}
                                />
                                <span className="input-group-text">sec</span>
                                {errors.chatTimerMaxPauseLimit && (
                                    <div className="invalid-feedback">
                                        {errors.chatTimerMaxPauseLimit.message}
                                    </div>
                                )}
                            </div>
                            <small className="text-muted mt-2 d-block">
                                Maximum pause time in seconds for a chat consultation when viewing a Pooja details page
                            </small>
                        </div>
                        <div className="col-12 mt-4 text-end">
                            <button type="submit" className="btn btn-primary">
                                Save Changes
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ChatTimerMaxPauseLimitConfig;
